import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from 'react'
import { fetchProfile } from '../api/api'
import { AxiosError } from 'axios'

// Типы для профиля пользователя
interface Character {
	Name: string
	CLevel: number
	Class: string
	ResetCount?: number
	Guild?: string | null
	Online?: boolean
}

interface UserProfile {
	username: string
	characters: Character[]
}

// Интерфейс для контекста
interface AuthContextType {
	token: string | null
	profile: UserProfile | null
	loading: boolean // Добавляем состояние загрузки
	setToken: React.Dispatch<React.SetStateAction<string | null>>
	setProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>
	handleLogin: (newToken: string) => void
	handleLogout: () => void
}

// Создаём контекст с начальным значением undefined
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Хук для удобного доступа к контексту
export const useAuth = () => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}

// Провайдер контекста
interface AuthProviderProps {
	children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [token, setToken] = useState<string | null>(
		localStorage.getItem('token')
	)
	const [profile, setProfile] = useState<UserProfile | null>(null)
	const [loading, setLoading] = useState<boolean>(true) // Добавляем состояние загрузки

	// Загружаем профиль при наличии токена
	useEffect(() => {
		const fetchProtectedData = async () => {
			if (!token) {
				setProfile(null)
				setLoading(false) // Если токена нет, загрузка завершена
				return
			}
			try {
				setLoading(true) // Начинаем загрузку
				const profileResponse = await fetchProfile()
				setProfile(profileResponse.data)
			} catch (error) {
				const axiosError = error as AxiosError
				console.error('Error fetching protected data:', axiosError)
				setProfile(null)
				if (axiosError.response?.status === 401) {
					console.log('Token expired or invalid, logging out')
					localStorage.removeItem('token')
					setToken(null)
				}
			} finally {
				setLoading(false) // Завершаем загрузку
			}
		}

		fetchProtectedData()

		const handleUnauthorized = () => {
			console.log('Handling unauthorized event')
			localStorage.removeItem('token')
			setToken(null)
			setProfile(null)
			setLoading(false)
		}

		window.addEventListener('unauthorized', handleUnauthorized)

		return () => {
			window.removeEventListener('unauthorized', handleUnauthorized)
		}
	}, [token])

	const handleLogin = (newToken: string) => {
		setToken(newToken)
		localStorage.setItem('token', newToken)
	}

	const handleLogout = () => {
		setToken(null)
		localStorage.removeItem('token')
		setProfile(null)
	}

	return (
		<AuthContext.Provider
			value={{
				token,
				profile,
				loading,
				setToken,
				setProfile,
				handleLogin,
				handleLogout,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}
